import type { ClassSession, UserTrainingClass } from 'pollworker/src/state/training';
import { TrainingClassStatus } from 'pollworker/src/state/training';
import { LmsEnrollmentStatus } from 'shared/src/utils/lmsStatus';

export interface RemoteTrainingRow {
  trainingClass: UserTrainingClass;
  session: ClassSession;
  lms: NonNullable<ClassSession['Lms']>;
}

export interface GroupedTrainingClasses {
  incompleteRemote: RemoteTrainingRow[];
  unscheduledRemote: RemoteTrainingRow[];
  unscheduled: UserTrainingClass[];
  booked: UserTrainingClass[];
  completedRemote: RemoteTrainingRow[];
  completedInPerson: UserTrainingClass[];
}

export function getRemoteDisplaySession(trainingClass: UserTrainingClass, lmsEnabled: boolean) {
  if (!lmsEnabled || trainingClass.Status === TrainingClassStatus.Cancelled) return undefined;
  if (trainingClass.SelectedSession?.Lms) return trainingClass.SelectedSession;

  const remoteSessions = trainingClass.AvailableSessions.filter((session) => session.Lms);
  const enrolledSession = remoteSessions.find(
    (session) => session.Lms?.EnrollmentStatus === LmsEnrollmentStatus.InProgress || session.Lms?.EnrollmentStatus === LmsEnrollmentStatus.Completed,
  );
  if (enrolledSession) return enrolledSession;

  const inPersonSessions = trainingClass.AvailableSessions.filter((session) => !session.Lms);
  return remoteSessions.length === 1 && inPersonSessions.length === 0 ? remoteSessions[0] : undefined;
}

export function hasOnlineOption(trainingClass: UserTrainingClass, lmsEnabled: boolean) {
  return lmsEnabled && trainingClass.AvailableSessions.some((session) => session.Lms);
}

export function getRemoteCourseTitle(session: ClassSession, className?: string) {
  const overlay = session.Lms;
  const learnHouseName = overlay?.CourseName?.trim();
  if (learnHouseName && learnHouseName !== overlay?.LhCourseUuid) return learnHouseName;
  if (className?.trim()) return className;
  return 'Online course';
}

function toRemoteRow(trainingClass: UserTrainingClass, session: ClassSession): RemoteTrainingRow | undefined {
  if (!session.Lms) return undefined;
  return { trainingClass, session, lms: session.Lms };
}

export function groupTrainingClasses(trainingClasses: UserTrainingClass[], lmsEnabled: boolean): GroupedTrainingClasses {
  const grouped: GroupedTrainingClasses = {
    incompleteRemote: [],
    unscheduledRemote: [],
    unscheduled: [],
    booked: [],
    completedRemote: [],
    completedInPerson: [],
  };

  for (const trainingClass of trainingClasses) {
    const remoteSession = getRemoteDisplaySession(trainingClass, lmsEnabled);
    const remoteRow = remoteSession ? toRemoteRow(trainingClass, remoteSession) : undefined;
    const isFinished =
      trainingClass.Status === TrainingClassStatus.Cancelled ||
      trainingClass.Status === TrainingClassStatus.Completed ||
      remoteRow?.lms.EnrollmentStatus === LmsEnrollmentStatus.Completed;

    if (isFinished) {
      if (remoteRow) grouped.completedRemote.push(remoteRow);
      else grouped.completedInPerson.push(trainingClass);
      continue;
    }

    if (trainingClass.Status === TrainingClassStatus.Unavailable) {
      grouped.unscheduled.push(trainingClass);
      continue;
    }

    if (remoteRow) {
      if (trainingClass.SessionId || remoteRow.lms.EnrollmentStatus === LmsEnrollmentStatus.InProgress) {
        grouped.incompleteRemote.push(remoteRow);
      } else {
        grouped.unscheduledRemote.push(remoteRow);
      }
      continue;
    }

    if (trainingClass.Status === TrainingClassStatus.Scheduled) {
      grouped.booked.push(trainingClass);
      continue;
    }

    grouped.unscheduled.push(trainingClass);
  }

  return grouped;
}
